import {
  CellEditingStartedEvent,
  CellEditingStoppedEvent,
  ColDef,
  DomLayoutType,
  GridApi,
  RowDataUpdatedEvent,
  RowHeightParams,
  RowSelectionOptions,
  SortDirection,
} from "ag-grid-community";
import { DatePickerProps, InputProps, SelectProps, TabsProps } from "antd";
import { QueryParamConfig } from "use-query-params";
import { DefaultOptionType } from "antd/es/select";
import { ReactNode } from "react";

export interface PaginationQuery {
  size: number;
  page: number;
  sorts: string[];
  dirs: SortDirection[];
  includeAll?: boolean;
}

export interface PageData<T> extends Omit<PaginationQuery, "sorts" | "dirs"> {
  items: T[];
  pages: number;
  total: number;
  sizes: number[];
}

export const DefaultPageSizes = [10, 20, 50];

export const DefaultPageData: PageData<any> = {
  items: [],
  total: 0,
  size: DefaultPageSizes[0],
  page: 1,
  pages: 0,
  sizes: DefaultPageSizes,
};

export type TablePageData<T> = PageData<T>;

export interface SortColDef<TData = any, TValue = any> extends ColDef<
  TData,
  TValue
> {
  sortKey?: string;
}

export interface BasicProps {
  id?: string;
  className?: string;
  children?: React.ReactNode;
  style?: React.CSSProperties;
}

export interface BasicTableProps<T> extends BasicProps {
  loading: boolean;
  columns: SortColDef<T>[];
  page?: TablePageData<T>;
  getRowId: (row: T) => string;
  rowHeight?: number;
  getRowHeight?: (params: RowHeightParams<T>) => number | undefined | null;
  enableRowDetail: boolean;
  detailRowHeight?: number;
  rowDetailRender?: (data: T | undefined) => React.ReactNode;
  loadingTemplate?: React.FC;
  noDataTemplate?: string;
  enablePagination: boolean;
  onPaginationChange?: (page: number, size: number) => void;
  onRowDataUpdated?: (event: RowDataUpdatedEvent<T>) => void;
  paginationClassnames?: string;
  showRefresh?: boolean;
  onRefreshData?: () => void;
  onSortChange?: (sorts: string[], dirs: SortDirection[]) => void;
  domLayout?: DomLayoutType;
  suppressClickEdit?: boolean;
  onCellEditingStarted?: (event: CellEditingStartedEvent<T>) => void;
  onCellEditingStopped?: (event: CellEditingStoppedEvent<T>) => void;
  getRowStyle?: () => any;
  selectionMode?: RowSelectionOptions<T> | "single" | "multiple";
  onRowSelected?: (data: T | undefined) => void;
  selectedRowIds?: number[];
}

export interface BasicTableRef {
  resetRowHeights: () => void;
  getAgGridApi: () => GridApi;
}

export enum LoadingStatus {
  loading,
  succeeded,
  failed,
}

export interface Response<T> {
  success: boolean;
  msg: string | string[];
  data: T;
}

export type PageResponse<T> = Response<PageData<T>>;

export interface SearchItemBasic<T = unknown> {
  title: string;
  desc?: string;
  span: 1 | 2 | 3 | 4;
  type:
    | "text"
    | "date"
    | "dateRange"
    | "select"
    | "number"
    | "switch"
    | "buttons"
    | "radio";
  parentKey?: string;
  key: string;
  defaultValue?: T;
  pageQueryConfig?: QueryParamConfig<
    T | null | undefined,
    T | null | undefined
  >;
  validate?: (value: T) => [boolean, string | undefined];
  className?: string;
}

export interface SearchTextItem<T = string> extends SearchItemBasic<T> {
  placeHolder?: string;
  inputMode?: InputProps["inputMode"];
  upper?: boolean;
}

export interface SearchDateItem<T = Date> extends SearchItemBasic<T> {
  mode: DatePickerProps["mode"];
  showTime: boolean;
  placeHolder?: string;
}

export interface SearchSelectItem<T = any> extends SearchItemBasic<T> {
  options: T[] | ((searchValue?: string) => Promise<T[]>);
  preloadOptions?: boolean;
  mode?: Exclude<SelectProps["mode"], "combobox">;
  dataToOption?: (data: T) => DefaultOptionType;
  placeHolder?: string;
  lazy?: boolean;
  showSearch?: boolean;
}

export interface SearchSwitchItem<T = string | boolean | number> extends Omit<
  SearchItemBasic<T>,
  "pageQueryConfig"
> {
  checkedValue: T;
  checkedTitle: string;
  unCheckedValue: T;
  unCheckedTitle: string;
  pageQueryConfig?:
    | QueryParamConfig<string, string>
    | QueryParamConfig<number, number>
    | QueryParamConfig<boolean, boolean>;
}

export interface SearchRadioItem<T = string | number> extends Omit<
  SearchItemBasic<T>,
  "pageQueryConfig"
> {
  options: { title: string; value: T }[];
  pageQueryConfig?:
    | QueryParamConfig<string, string>
    | QueryParamConfig<number, number>;
}

export interface SearchButtonsItem extends Omit<
  SearchItemBasic<any>,
  "defaultValue" | "validate"
> {
  render: (loading: boolean) => React.ReactNode;
}

export type SearchItem =
  | SearchTextItem
  | SearchDateItem
  | SearchSelectItem
  | SearchSwitchItem
  | SearchRadioItem
  | SearchButtonsItem;

export interface SearchFormDefaultProps {
  hasCollspaed?: boolean;
  collapsed?: boolean;
}

export interface SearchFormParams<T> extends BasicProps {
  items: SearchItem[];
  onFormChange: (key: string, value: unknown) => void;
  values: T;
  defaultConfig?: SearchFormDefaultProps;
}

export interface SearchTableItemParams<T, K> {
  loading: boolean;
  onItemValueChange: (value: T) => void;
  value: T;
  showHeader?: boolean;
  dependent?: React.ReactNode;
  config: K;
}

export interface DialogRef {
  showModal: (props?: unknown) => void;
  hideModal: () => void;
}

export interface ModalProp {
  key: string;
  label: string;
  hasCopy?: boolean;
  convertor?: (value: string) => string | React.ReactNode;
}

export type CommonTabConfig = {
  key: string;
  label: ReactNode;
  icon?: ReactNode;
  content?: React.ReactNode;
};

export type CommonTabProps = {
  active: string;
  className?: string;
  tabs: CommonTabConfig[];
  size?: TabsProps["size"];
  tabBarExtraContent?: TabsProps["tabBarExtraContent"];
  onTabChange?: (key: string) => void;
  hasContent?: boolean;
};
